import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { useApiClient } from "../client";
import { 
  CreateCheckoutSessionRequestDto, 
  CreateCheckoutSessionResponseDto,
  ConfirmCheckoutSessionRequestDto,
  ConfirmCheckoutSessionResponseDto,
  PortalSessionResponseDto,
  CancelTrialResponseDto
} from "../interfaces/billing.interface";

/**
 * Create a new billing checkout session
 */
export function useBillingCreateCheckout(
  options?: Omit<
    UseMutationOptions<
      CreateCheckoutSessionResponseDto,
      Error,
      CreateCheckoutSessionRequestDto
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useMutation({
    ...options,
    mutationFn: (data: CreateCheckoutSessionRequestDto) =>
      apiRequest<CreateCheckoutSessionResponseDto>(
        `/billing/checkout`,
        "POST",
        data,
      ),
  });
}

/**
 * Confirm a checkout session after the Stripe success redirect.
 */
export function useBillingConfirmCheckout(
  options?: Omit<
    UseMutationOptions<
      ConfirmCheckoutSessionResponseDto,
      Error,
      ConfirmCheckoutSessionRequestDto
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useMutation({
    ...options,
    mutationFn: (data: ConfirmCheckoutSessionRequestDto) =>
      apiRequest<ConfirmCheckoutSessionResponseDto>(
        `/billing/checkout/confirm`,
        "POST",
        data,
        undefined,
        {
          skipGlobalErrorHandler: true,
          throwOnError: true,
        },
      ),
  });
}

/**
 * Get customer portal session URL
 */
export function useBillingPortalSession(
  options?: Omit<
    UseMutationOptions<
      PortalSessionResponseDto,
      Error,
      void
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useMutation({
    ...options,
    mutationFn: () =>
      apiRequest<PortalSessionResponseDto>(
        `/billing/portal`,
        "POST",
      ),
  });
}

/**
 * Change plan (smart redirect to portal or checkout)
 */
export function useBillingChangePlan(
  options?: Omit<
    UseMutationOptions<
      CreateCheckoutSessionResponseDto,
      Error,
      CreateCheckoutSessionRequestDto
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useMutation({
    ...options,
    mutationFn: (data: CreateCheckoutSessionRequestDto) =>
      apiRequest<CreateCheckoutSessionResponseDto>(
        `/billing/change-plan`,
        "POST",
        data,
      ),
  });
}

/**
 * Cancel trial
 */
export function useBillingCancelTrial(
  options?: Omit<
    UseMutationOptions<
      CancelTrialResponseDto,
      Error,
      void
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useMutation({
    ...options,
    mutationFn: () =>
      apiRequest<CancelTrialResponseDto>(
        `/billing/cancel-trial`,
        "POST",
      ),
  });
}
